// Per-customer CALL-OUT rate override section (Session ADB), shown on the customer detail page for
// ADMINS only. It shows the standard Schedule of Fees rates and, when set, the customer's negotiated
// override alongside them, with Add / Edit / Delete. A blank field uses the standard rate. This
// applies ONLY to call-out charges, never the maintenance quote engine. Self-contained: it fetches
// its own data and reports the active state up so the customer header can flag it with a pill.
import { useEffect, useState } from "react";
import { ApiError, api } from "../../lib/api";
import type { RateOverridePayload, RateOverrideInput } from "../../lib/api";
import { ConfirmButton } from "../../components/ConfirmButton";
import { Button, buttonClasses } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { StatusPill } from "../../components/StatusPill";

// Pence -> "£1,234.56".
function gbp(pence: number): string {
  return `£${(pence / 100).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Pence -> a plain GBP input string ("163.00"), or "" for null.
function penceToInput(pence: number | null): string {
  return pence == null ? "" : (pence / 100).toFixed(2);
}

// A trimmed GBP string -> pence (rounded), or null when blank. Returns "invalid" for a bad number.
function inputToPence(s: string): number | null | "invalid" {
  const t = s.trim();
  if (t === "") return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0) return "invalid";
  return Math.round(n * 100);
}

// A trimmed multiplier string -> factor, or null when blank; "invalid" outside [1, 10].
function inputToMultiplier(s: string): number | null | "invalid" {
  const t = s.trim();
  if (t === "") return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n < 1 || n > 10) return "invalid";
  return n;
}

interface FormState {
  firstHour: string;
  additionalHour: string;
  oohMultiplier: string;
  travelPerHour: string;
  notes: string;
}

const EMPTY_FORM: FormState = { firstHour: "", additionalHour: "", oohMultiplier: "", travelPerHour: "", notes: "" };

export function CustomerRateOverrideSection({ customerId, onActiveChange }: { customerId: string; onActiveChange?: (active: boolean) => void }) {
  const [payload, setPayload] = useState<RateOverridePayload | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const override = payload?.override ?? null;
  const standard = payload?.standard ?? null;

  useEffect(() => {
    let live = true;
    setState("loading");
    api.customers.rateOverride
      .get(customerId)
      .then((p) => {
        if (!live) return;
        setPayload(p);
        setState("ready");
        onActiveChange?.(p.override != null);
      })
      .catch(() => live && setState("error"));
    return () => {
      live = false;
    };
    // onActiveChange is a stable setter; re-run only on customer change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  function startAdd() {
    setForm(EMPTY_FORM);
    setFormError(null);
    setEditing(true);
  }

  function startEdit() {
    if (!override) return;
    setForm({
      firstHour: penceToInput(override.firstHourPence),
      additionalHour: penceToInput(override.additionalHourPence),
      oohMultiplier: override.oohMultiplier == null ? "" : String(override.oohMultiplier),
      travelPerHour: penceToInput(override.travelPerHourPence),
      notes: override.notes ?? "",
    });
    setFormError(null);
    setEditing(true);
  }

  async function save() {
    const firstHourPence = inputToPence(form.firstHour);
    const additionalHourPence = inputToPence(form.additionalHour);
    const travelPerHourPence = inputToPence(form.travelPerHour);
    const oohMultiplier = inputToMultiplier(form.oohMultiplier);
    if (firstHourPence === "invalid" || additionalHourPence === "invalid" || travelPerHourPence === "invalid") {
      setFormError("Enter each rate as a positive amount in pounds, or leave it blank to use the standard rate.");
      return;
    }
    if (oohMultiplier === "invalid") {
      setFormError("The out-of-hours multiplier must be between 1 and 10 (2 is double time). Leave it blank to use the standard 2x.");
      return;
    }
    const body: RateOverrideInput = {
      firstHourPence,
      additionalHourPence,
      travelPerHourPence,
      oohMultiplier,
      notes: form.notes.trim() === "" ? null : form.notes.trim(),
    };
    setSaving(true);
    setFormError(null);
    try {
      const result = override ? await api.customers.rateOverride.update(customerId, body) : await api.customers.rateOverride.create(customerId, body);
      setPayload(result);
      onActiveChange?.(result.override != null);
      setEditing(false);
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message || err.code : "Could not save the rate override. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    setDeleting(true);
    try {
      await api.customers.rateOverride.remove(customerId);
      setPayload((p) => (p ? { ...p, override: null } : p));
      onActiveChange?.(false);
      setEditing(false);
    } catch {
      setFormError("Could not remove the rate override. Please try again.");
    } finally {
      setDeleting(false);
    }
  }

  const inputClass = "w-full rounded-ele border border-ele-purple-accent bg-ele-field-inset px-3 py-1.5 text-sm text-ele-strong outline-none focus:border-ele-green";

  // One row: the standard value and, when overridden, the negotiated value with a "Custom" pill.
  function Row({ label, standardText, custom }: { label: string; standardText: string; custom: string | null }) {
    return (
      <div className="flex items-center justify-between gap-3 border-b border-ele-purple-accent py-1.5 last:border-0">
        <dt className="text-ele-body">{label}</dt>
        <dd className="flex items-center gap-2 text-right">
          {custom != null ? (
            <>
              <span className="text-ele-strong">{custom}</span>
              <StatusPill word="Custom" tone="green" />
            </>
          ) : (
            <span className="text-ele-strong">{standardText}</span>
          )}
        </dd>
      </div>
    );
  }

  return (
    <section className="rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h2 className="font-heading text-lg text-ele-blue">Rate override</h2>
          {override != null && <StatusPill word="Active" tone="green" />}
        </div>
        {state === "ready" && !editing && (
          <div className="flex items-center gap-2">
            {override == null ? (
              <Button variant="secondary" size="sm" onClick={startAdd}>
                Add rate override
              </Button>
            ) : (
              <>
                <button
                  onClick={startEdit}
                  className="rounded-ele border border-ele-purple-accent px-3 py-1 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green"
                >
                  Edit
                </button>
                <ConfirmButton
                  onConfirm={remove}
                  busy={deleting}
                  label="Delete"
                  className={buttonClasses("destructive", "sm")}
                />
              </>
            )}
          </div>
        )}
      </div>

      <p className="mb-3 text-xs text-ele-body">
        Negotiated call-out rates for this customer (for example a managing agent or housing
        association with agreed rates). Applies to call-out charges only, not to maintenance quotes.
        A blank field uses the standard Schedule of Fees rate.
      </p>
      {/*
        Director decision D2 (Session AIC). The form gave no answer to the question office staff
        actually have when typing a figure here, and the platform was answering it the other way:
        a negotiated GBP 200 first hour used to bill a Care customer GBP 170. Say it in words on
        the form itself, so nobody has to infer the rule from an invoice.
      */}
      <p className="mb-3 text-xs text-ele-body">
        A figure typed here is the amount billed. The tier discount (Care 15%, Complete 25%) is not
        applied on top of it. A blank first-hour or additional-hour field still takes the standard
        rate with that customer's tier discount applied. Travel is never tier-discounted either way.
        Non-contract call-outs always bill the published non-contract card, whatever is set here.
      </p>

      {state === "loading" ? (
        <Loader shape="inline" />
      ) : state === "error" ? (
        <p role="alert" className="text-sm text-ele-danger">Could not load the rate override. Please try again.</p>
      ) : !standard ? null : editing ? (
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body">First hour (GBP)</span>
              <input type="number" min={0} step="0.01" inputMode="decimal" value={form.firstHour} onChange={(e) => setForm((f) => ({ ...f, firstHour: e.target.value }))} placeholder={gbp(standard.firstHourPence).replace("£", "")} className={inputClass} />
            </label>
            <label className="block">
              <span className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body">Additional hour (GBP)</span>
              <input type="number" min={0} step="0.01" inputMode="decimal" value={form.additionalHour} onChange={(e) => setForm((f) => ({ ...f, additionalHour: e.target.value }))} placeholder={gbp(standard.additionalHourPence).replace("£", "")} className={inputClass} />
            </label>
            <label className="block">
              <span className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body">Out of hours multiplier</span>
              <input type="number" min={1} max={10} step="0.1" inputMode="decimal" value={form.oohMultiplier} onChange={(e) => setForm((f) => ({ ...f, oohMultiplier: e.target.value }))} placeholder={String(standard.oohMultiplier)} className={inputClass} />
            </label>
            <label className="block">
              <span className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body">Travel per hour (GBP)</span>
              <input type="number" min={0} step="0.01" inputMode="decimal" value={form.travelPerHour} onChange={(e) => setForm((f) => ({ ...f, travelPerHour: e.target.value }))} placeholder={gbp(standard.travelPerHourPence).replace("£", "")} className={inputClass} />
            </label>
          </div>
          <label className="block">
            <span className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body">Notes (optional)</span>
            <textarea rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="For example the agreement reference or who negotiated it." className={inputClass} />
          </label>
          {formError && <p role="alert" className="text-sm text-ele-danger">{formError}</p>}
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={save} disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
            <button onClick={() => { setEditing(false); setFormError(null); }} disabled={saving} className="rounded-ele border border-ele-purple-accent px-4 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-strong disabled:opacity-50">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          <dl className="text-sm">
            <Row label="First hour" standardText={gbp(standard.firstHourPence)} custom={override?.firstHourPence != null ? gbp(override.firstHourPence) : null} />
            <Row label="Additional hour" standardText={gbp(standard.additionalHourPence)} custom={override?.additionalHourPence != null ? gbp(override.additionalHourPence) : null} />
            <Row label="Out of hours" standardText={`${standard.oohMultiplier}x`} custom={override?.oohMultiplier != null ? `${override.oohMultiplier}x` : null} />
            <Row label="Travel per hour" standardText={gbp(standard.travelPerHourPence)} custom={override?.travelPerHourPence != null ? gbp(override.travelPerHourPence) : null} />
          </dl>
          {override == null ? (
            <p className="mt-3 text-xs text-ele-body">No custom rates. This customer is charged the standard Schedule of Fees.</p>
          ) : override.notes && override.notes.trim() ? (
            <p className="mt-3 border-t border-ele-purple-accent pt-3 text-sm text-ele-body">{override.notes}</p>
          ) : null}
        </>
      )}
    </section>
  );
}
